/**
 * BB Tabbed Product Slider
 * - Accessible tabs (WAI-ARIA tabs pattern, arrow-key navigation)
 * - Swiper 4 slider per tab panel, initialised lazily on first activation
 * - Swiper is loaded globally by the theme; we do not re-import it
 */
(function () {
  'use strict';

  var ready = function (fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  };

  function boolAttr(el, name) {
    var v = el.getAttribute(name);
    return v === 'true' || v === '1';
  }

  function waitForSwiper(cb, tries) {
    if (typeof window.Swiper !== 'undefined') {
      cb();
      return;
    }
    if (tries === undefined) tries = 0;
    if (tries > 80) return;
    setTimeout(function () {
      waitForSwiper(cb, tries + 1);
    }, 100);
  }

  /* ------------------------------------------- "For You" (recently viewed) */

  function safeJsonParse(v) {
    try {
      return JSON.parse(v);
    } catch (e) {
      return null;
    }
  }

  function getRecentlyViewed() {
    var arr = safeJsonParse(window.localStorage && window.localStorage.getItem('recently_viewed'));
    if (!Array.isArray(arr)) return [];

    // Some implementations store current PDP as first item; also guard empties
    return arr
      .filter(function (x) {
        return x && (x.productUrl || x.productImg || x.productTitle);
      })
      .map(function (x) {
        return {
          url: x.productUrl || '',
          img: x.productImg || '',
          titleHtml: x.productTitle || '',
          metaHtml: x.productmeta || '',
        };
      })
      .filter(function (x) {
        return typeof x.url === 'string' && x.url.indexOf('/products/') !== -1;
      });
  }

  function uniqByUrl(items) {
    var seen = {};
    return items.filter(function (it) {
      if (!it.url) return false;
      if (seen[it.url]) return false;
      seen[it.url] = true;
      return true;
    });
  }

  function extractHandle(productUrl) {
    // works for /products/handle or full URL
    var m = String(productUrl).match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  function moneyFormat(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      // Use theme money format if available
      var fmt =
        window.theme && window.theme.money_format
          ? window.theme.money_format
          : window.Shopify.money_format || '${{amount}}';
      return window.Shopify.formatMoney(cents, fmt);
    }
    // Fallback: simple dollars
    return '$' + (cents / 100).toFixed(2);
  }

  function fetchProductCardHtml(handle) {
    // Uses `templates/product.bb-tabbed-card.liquid` (layout none) to return the exact `product-block` markup
    return fetch('/products/' + handle + '?view=bb-tabbed-card', { credentials: 'same-origin' })
      .then(function (r) {
        // A retired product handle can 301-redirect to a page (e.g. /pages/...) which returns
        // 200 OK with a full document. Reject redirects and non-OK responses so we never inject
        // an entire page (header/nav/footer) into a carousel slide.
        if (!r.ok || r.redirected) return '';
        return r.text();
      })
      .then(function (html) {
        // Guard: the card template must be a fragment, not a full HTML document.
        if (/<(html|head|meta|body|title)\b/i.test(html)) return '';
        return html;
      })
      .catch(function () {
        return '';
      });
  }

  function buildForYouSlideHtmlFromCardHtml(cardHtml) {
    var html = typeof cardHtml === 'string' ? cardHtml.trim() : '';
    if (!html) return '';

    // Ensure we wrap in the exact same slide structure as other tabs
    return '<div class="swiper-slide bb-tabbed-slider__slide">' + html + '</div>';
  }

  function renderForYou(section) {
    var tab = section.querySelector('[data-for-you-tab]');
    var panel = section.querySelector('[data-for-you="true"]');
    var mount = section.querySelector('[data-for-you-panel]');
    if (!tab || !panel || !mount) return;

    var items = uniqByUrl(getRecentlyViewed());
    var limit = parseInt(panel.getAttribute('data-products-limit') || '12', 10);
    if (!isFinite(limit) || limit < 1) limit = 12;
    items = items.slice(0, limit);

    if (items.length === 0) {
      // Remove tab & panel completely if no recently viewed
      tab.parentNode && tab.parentNode.removeChild(tab);
      panel.parentNode && panel.parentNode.removeChild(panel);
      return;
    }

    // Show tab (but keep selection unchanged)
    tab.hidden = false;

    // Build Swiper track with the same structure as `bb-tabbed-slider-track`
    var skeleton =
      '<div class="bb-tabbed-slider__swiper swiper-container" data-slider>' +
      '<div class="swiper-wrapper" data-for-you-wrapper></div>' +
      '<a href="#" class="swiper-button-prev bb-tabbed-slider__arrow bb-tabbed-slider__arrow--prev" aria-label="Previous products" data-slider-prev>' +
      '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" width="20" height="20">' +
      '<path d="M15 6l-6 6 6 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />' +
      '</svg>' +
      '</a>' +
      '<a href="#" class="swiper-button-next bb-tabbed-slider__arrow bb-tabbed-slider__arrow--next" aria-label="Next products" data-slider-next>' +
      '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" width="20" height="20">' +
      '<path d="M9 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />' +
      '</svg>' +
      '</a>' +
      '<div class="swiper-pagination bb-tabbed-slider__dots" data-slider-pagination></div>' +
      '</div>';

    mount.innerHTML = skeleton;
    var wrapper = mount.querySelector('[data-for-you-wrapper]');
    if (!wrapper) return;

    // Fetch product JSON for better accuracy (price/url/image). If fetch fails, we still render from localStorage.
    Promise.all(
      items.map(function (it) {
        var handle = extractHandle(it.url);
        if (!handle) return Promise.resolve('');
        return fetchProductCardHtml(handle);
      })
    ).then(function (rows) {
      var slides = rows
        .map(buildForYouSlideHtmlFromCardHtml)
        .filter(function (x) {
          return x;
        })
        .join('');

      wrapper.innerHTML = slides;
    });
  }

  /* --------------------------------------------------------- Swiper per panel */

  function buildSwiperOptions(section, sliderRoot) {
    var autoplay = boolAttr(section, 'data-autoplay');
    var autoplaySpeed = parseInt(section.getAttribute('data-autoplay-speed') || '5000', 10);
    var showArrows = boolAttr(section, 'data-show-arrows');
    var showDots = boolAttr(section, 'data-show-dots');
    var loop = boolAttr(section, 'data-loop');

    /*
     * Swiper 4 breakpoint keys are MAX widths (unlike Swiper 5+).
     * Main config = desktop defaults (4.5 slides),
     * `breakpoints.767` overrides for mobile (1.5 slides).
     */
    var opts = {
      slidesPerView: 4.5,
      spaceBetween: 24,
      loop: loop,
      watchOverflow: true,
      observer: true,
      observeParents: true,
      resizeReInit: true,
      grabCursor: true,
      // Swiper 4 defaults these on: preloadImages copies every <img> in the
      // slider into a new Image(), downloading all cards (and their hidden
      // hover images) up front and defeating loading="lazy". Card sizes come
      // from aspect-ratio wrappers, so nothing needs to wait for images.
      preloadImages: false,
      updateOnImagesReady: false,
      breakpoints: {
        767: {
          slidesPerView: 1.5,
          spaceBetween: 12,
        },
      },
      a11y: {
        prevSlideMessage: 'Previous products',
        nextSlideMessage: 'Next products',
      },
    };

    // Reduced motion: never auto-advance (WCAG 2.2.2); arrows/dots still work
    if (autoplay && autoplaySpeed > 0 && !prefersReducedMotion()) {
      opts.autoplay = {
        enabled: true,
        delay: autoplaySpeed,
        disableOnInteraction: false,
      };
    } else {
      // Swiper 4 turns autoplay on for any autoplay object that omits `enabled`.
      // Pass false so the module normalizer stores { enabled: false }.
      opts.autoplay = false;
    }

    if (showArrows) {
      opts.navigation = {
        prevEl: sliderRoot.querySelector('[data-slider-prev]'),
        nextEl: sliderRoot.querySelector('[data-slider-next]'),
      };
    } else {
      var prev = sliderRoot.querySelector('[data-slider-prev]');
      var next = sliderRoot.querySelector('[data-slider-next]');
      if (prev) prev.hidden = true;
      if (next) next.hidden = true;
    }

    if (showDots) {
      opts.pagination = {
        el: sliderRoot.querySelector('[data-slider-pagination]'),
        clickable: true,
      };
    } else {
      var pag = sliderRoot.querySelector('[data-slider-pagination]');
      if (pag) pag.hidden = true;
    }

    return opts;
  }

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  // Autoplay runs only while the panel is the active tab, on screen, not
  // hovered/focused, and the browser tab is visible.
  function canAutoplay(panel) {
    var swiper = panel && panel._bbSwiper;
    return !!(
      swiper &&
      swiper.autoplay &&
      swiper.params.autoplay &&
      !panel.hasAttribute('hidden') &&
      !panel._bbOffscreen &&
      !panel._bbHovered &&
      !panel._bbFocused &&
      !document.hidden
    );
  }

  function syncAutoplay(panel) {
    if (!panel || !panel._bbSwiper || !panel._bbSwiper.autoplay) return;
    if (canAutoplay(panel)) panel._bbSwiper.autoplay.start();
    else panel._bbSwiper.autoplay.stop();
  }

  function initPanelSwiper(panel, section) {
    if (!panel || panel._bbSwiper) return;
    var sliderRoot = panel.querySelector('[data-slider]');
    if (!sliderRoot) return;
    if (typeof window.Swiper === 'undefined') return;

    var opts = buildSwiperOptions(section, sliderRoot);
    panel._bbSwiper = new window.Swiper(sliderRoot, opts);

    if (!opts.autoplay && panel._bbSwiper.autoplay) {
      panel._bbSwiper.autoplay.stop();
    }

    // Swiper 4 has no native pauseOnMouseEnter — wire it up manually
    if (opts.autoplay && opts.autoplay.enabled && panel._bbSwiper.autoplay) {
      var pause = function () {
        if (panel._bbSwiper && panel._bbSwiper.autoplay) panel._bbSwiper.autoplay.stop();
      };
      var resume = function () {
        if (!panel.hasAttribute('hidden') && panel._bbSwiper && panel._bbSwiper.autoplay) {
          panel._bbSwiper.autoplay.start();
        }
      };
      sliderRoot.addEventListener('mouseenter', pause);
      sliderRoot.addEventListener('mouseleave', resume);
      sliderRoot.addEventListener('focusin', pause);
      sliderRoot.addEventListener('focusout', resume);
    }

    requestAnimationFrame(function () {
      if (panel._bbSwiper && typeof panel._bbSwiper.update === 'function') {
        panel._bbSwiper.update();
      }
    });
  }

  function refreshPanelSwiper(panel, section) {
    if (!panel || !panel._bbSwiper) return;
    if (typeof panel._bbSwiper.update === 'function') panel._bbSwiper.update();
    if (!panel._bbSwiper.autoplay) return;
    if (
      section &&
      boolAttr(section, 'data-autoplay') &&
      panel._bbSwiper.params.autoplay &&
      panel._bbSwiper.params.autoplay.enabled
    ) {
      panel._bbSwiper.autoplay.start();
    } else {
      panel._bbSwiper.autoplay.stop();
    }
  }

  function pausePanelSwiper(panel) {
    if (!panel || !panel._bbSwiper) return;
    if (panel._bbSwiper.autoplay) panel._bbSwiper.autoplay.stop();
  }

  /* ----------------------------------------------------------- Tab controller */

  function TabController(section) {
    this.section = section;
    this.tabList = section.querySelector('[role="tablist"]');
    this.tabs = Array.prototype.slice.call(section.querySelectorAll('[role="tab"]'));
    this.panels = Array.prototype.slice.call(section.querySelectorAll('[role="tabpanel"]'));
  }

  TabController.prototype.init = function () {
    if (!this.tabList || this.tabs.length === 0) return;
    var self = this;

    waitForSwiper(function () {
      var activePanel =
        self.panels.find(function (p) {
          return !p.hasAttribute('hidden');
        }) || self.panels[0];
      if (activePanel) initPanelSwiper(activePanel, self.section);
    });

    this.tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        self.activate(tab, true);
      });
    });

    this.tabList.addEventListener('keydown', this.onKeyDown.bind(this));
  };

  TabController.prototype.activate = function (tab, focus) {
    var self = this;
    var controlsId = tab.getAttribute('aria-controls');

    this.tabs.forEach(function (t) {
      var isActive = t === tab;
      t.setAttribute('aria-selected', isActive ? 'true' : 'false');
      t.setAttribute('tabindex', isActive ? '0' : '-1');
      t.classList.toggle('is-active', isActive);

      var label = t.querySelector('.bb-tabbed-slider__tab-label');
      if (label) {
        label.classList.toggle('is-active', isActive);
      }
    });

    this.panels.forEach(function (panel) {
      var isActive = panel.id === controlsId;
      panel.classList.toggle('is-active', isActive);
      if (isActive) panel.removeAttribute('hidden');
      else panel.setAttribute('hidden', '');
    });

    var activePanel = this.panels.find(function (p) {
      return p.id === controlsId;
    });
    waitForSwiper(function () {
      if (!activePanel) return;
      if (!activePanel._bbSwiper) initPanelSwiper(activePanel, self.section);
      else refreshPanelSwiper(activePanel, self.section);
    });

    this.panels.forEach(function (panel) {
      if (panel.id !== controlsId) pausePanelSwiper(panel);
    });

    if (focus) tab.focus();
    tab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  };

  TabController.prototype.onKeyDown = function (e) {
    var key = e.key;
    if (key !== 'ArrowRight' && key !== 'ArrowLeft' && key !== 'Home' && key !== 'End') return;

    var currentIndex = this.tabs.indexOf(document.activeElement);
    if (currentIndex === -1) return;

    var lastIndex = this.tabs.length - 1;
    var nextIndex = currentIndex;

    if (key === 'ArrowRight') nextIndex = currentIndex === lastIndex ? 0 : currentIndex + 1;
    else if (key === 'ArrowLeft') nextIndex = currentIndex === 0 ? lastIndex : currentIndex - 1;
    else if (key === 'Home') nextIndex = 0;
    else if (key === 'End') nextIndex = lastIndex;

    e.preventDefault();
    this.activate(this.tabs[nextIndex], true);
  };

  /* ------------------------------------------------------------------- boot */

  function initSection(section) {
    if (!section || section._bbTabCtrl) return;
    renderForYou(section);
    var ctrl = new TabController(section);
    ctrl.init();
    section._bbTabCtrl = ctrl;
  }

  ready(function () {
    document.querySelectorAll('[data-bb-tabbed-slider]').forEach(initSection);

    document.addEventListener('visibilitychange', function () {
      document.querySelectorAll('[data-bb-tabbed-slider]').forEach(function (section) {
        section.querySelectorAll('[role="tabpanel"]').forEach(function (panel) {
          if (!panel._bbSwiper || !panel._bbSwiper.autoplay) return;
          var allowAutoplay =
            boolAttr(section, 'data-autoplay') &&
            panel._bbSwiper.params.autoplay &&
            panel._bbSwiper.params.autoplay.enabled;
          if (document.hidden || !allowAutoplay) panel._bbSwiper.autoplay.stop();
          else if (!panel.hasAttribute('hidden')) panel._bbSwiper.autoplay.start();
        });
      });
    });
  });

  /* --------------------------------------------------- Shopify theme editor */

  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target.querySelector('[data-bb-tabbed-slider]');
    if (section) initSection(section);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var section = e.target.querySelector('[data-bb-tabbed-slider]');
    if (!section) return;
    section.querySelectorAll('[role="tabpanel"]').forEach(function (panel) {
      if (panel._bbObserver) {
        panel._bbObserver.disconnect();
        panel._bbObserver = null;
      }
      if (panel._bbSwiper) {
        try {
          panel._bbSwiper.destroy(true, true);
        } catch (err) {}
        panel._bbSwiper = null;
      }
    });
  });

  document.addEventListener('shopify:block:select', function (e) {
    var tabIndex =
      e.target.getAttribute('data-panel-index') || e.target.getAttribute('data-tab-index');
    var section = e.target.closest('[data-bb-tabbed-slider]');
    if (!section || tabIndex === null) return;
    var tab = section.querySelector('[role="tab"][data-tab-index="' + tabIndex + '"]');
    if (tab) tab.click();
  });
})();
